import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { useTranslate } from 'react-admin';
import { useBarIndicatorsColors } from '@/composables/useBarIndicatorsColors';
import { getDeviceStateColor } from '@/helpers/device/getDeviceStateColor';
import PropTypes from 'prop-types';
import { DrawerLink } from '@/components/global/drawers/DrawerLink';
import { Palette } from '@/themes/themeOverrides/Palette';
import { BREAKPOINTS } from '@/constants/IndicatorBarBreakpoints';

const DeviceStateIndicator = ({ record, stateLabel, sx }) => {
    const theme = useTheme();
    const translate = useTranslate();
    const binStateNumber = record.binState ?? 0;
    const isBinStateUnknown = !record.binState;
    const calculatedBarColors = useBarIndicatorsColors(binStateNumber, BREAKPOINTS, false, true);
    const barColors = isBinStateUnknown ? Array(4).fill(Palette.gray.gray400) : calculatedBarColors;
    const formattedState = record.state ? record.state.charAt(0).toUpperCase() + record.state.slice(1) : translate('common.priority.unknown');
    const binStateLabel = record.binState ? `${record.binState}%` : translate('common.priority.unknown');

    if (Array.isArray(record)) {
        return (
            <Box sx={{ width: '100%' }}>
                {record.map(item => (
                    <Box key={item.id} sx={{ borderBottom: record.length > 1 ? `2px solid ${Palette.gray.gray100}` : 'none', padding: '8px 0' }}>
                        <DrawerLink
                            resource='device'
                            source={item.id}
                            action='show'
                            relative
                            label={
                                <Typography
                                    variant='caption'
                                    sx={{ fontSize: '11px', lineHeight: '16px', fontWeight: '600', display: 'block', marginBottom: '4px' }}
                                >
                                    {item.serialNumber}
                                </Typography>
                            }
                        />
                        <DeviceStateIndicator key={item.id} record={item} stateLabel={stateLabel} sx={{ ...sx, gap: '3px' }} />
                    </Box>
                ))}
            </Box>
        );
    } else {
        return (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    '& .MuiTypography-root.MuiTypography-caption': {
                        fontSize: '11px',
                        color: Palette.black,
                    },
                    ...sx,
                }}
            >
                <Box className='state-details' sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Typography variant='caption'>
                        {stateLabel}
                        {formattedState}
                    </Typography>
                    <Box
                        sx={{
                            width: '12px',
                            height: '12px',
                            borderRadius: '3px',
                            ...getDeviceStateColor(record.state, theme),
                            outlineOffset: '-2px',
                        }}
                    ></Box>
                </Box>
                <Box
                    className='state-details'
                    sx={{
                        display: 'flex',
                        flexWrap: 'nowrap',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '4px',
                    }}
                >
                    <Typography variant='caption' sx={{ fontSize: '11px', color: Palette.black }}>
                        {translate('common.bag')}: {binStateLabel}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: '2px' }}>
                        {barColors.map((color, i) => (
                            <Box
                                key={i}
                                sx={{
                                    width: '3px',
                                    height: '12px',
                                    borderRadius: '2px',
                                    backgroundColor: color,
                                }}
                            />
                        ))}
                    </Box>
                </Box>
            </Box>
        );
    }
};

DeviceStateIndicator.propTypes = {
    record: PropTypes.oneOfType([
        PropTypes.shape({
            state: PropTypes.string,
            binState: PropTypes.number,
        }),
        PropTypes.arrayOf(
            PropTypes.shape({
                state: PropTypes.string,
                binState: PropTypes.number,
            }),
        ),
    ]).isRequired,
    stateLabel: PropTypes.string,
    binStateLabel: PropTypes.string,
    sx: PropTypes.object,
};

export default DeviceStateIndicator;
